import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// ---------------------- Variables

const enableStore = import.meta.env.VITE_CURRENT_MODE !== "production";

// ---------------------- Interfaces

interface DateBookings {
  [id: string]: string[];
}

interface BookingsCalendarState {
  // --------- State

  expandedDays: string[];
  expandedBookings: DateBookings;
  registeredDates: string[];
  registeredBookings: DateBookings;
  refetchFunction: (() => void) | null;
  selectedFilter: string;

  // --------- Actions

  setExpandedDay: (id: string) => void;
  setAllDaysExpanded: () => void;
  setNoDaysExpanded: () => void;
  isDayExpanded: (id: string) => boolean;

  setExpandedBooking: (dateId: string, bookingId: string) => void;
  isBookingExpanded: (dateId: string, bookingId: string) => boolean;

  registerDate: (dateId: string) => void;

  registerBooking: (dateId: string, bookingId: string) => void;

  reset: () => void;

  setRefetchFunction: (fn: () => void) => void;
  triggerRefetch: () => void;

  setSelectedFilter: (filter: string) => void;
}

// ---------------------- Registered

// ---------------------- Store

export const useBookingsCalendarStore = create<BookingsCalendarState>()(
  devtools(
    immer((set, get) => ({
      // --------- State

      expandedDays: [],
      expandedBookings: {},
      registeredDates: [],
      registeredBookings: {},
      refetchFunction: null,
      selectedFilter: "all",

      // --------- Actions

      setExpandedDay: (id: string) =>
        set(
          (state) => {
            if (state.expandedDays.includes(id)) {
              state.expandedDays = state.expandedDays.filter((item) => item !== id);
              delete state.expandedBookings[id];
            } else {
              state.expandedDays.push(id);
            }
          },
          false,
          "bookings-calendar/setExpandedDay",
        ),

      isDayExpanded: (id: string) => {
        const state = get();
        return state.expandedDays.includes(id);
      },

      setAllDaysExpanded: () =>
        set(
          (state) => {
            state.expandedDays = [...state.registeredDates];
          },
          false,
          "bookings-calendar/setAllDaysExpanded",
        ),

      setNoDaysExpanded: () =>
        set(
          (state) => {
            for (let day = 0; day < state.expandedDays.length; day++) {
              state.expandedBookings[day] = [];
            }

            state.expandedDays = [];
          },
          false,
          "bookings-calendar/setNoDaysExpanded",
        ),

      setExpandedBooking: (dateId: string, bookingId: string) =>
        set(
          (state) => {
            if (!state.expandedBookings[dateId]) {
              state.expandedBookings[dateId] = [];
            }

            if (state.expandedBookings[dateId].includes(bookingId)) {
              state.expandedBookings[dateId] = state.expandedBookings[dateId].filter(
                (item) => item !== bookingId,
              );

              if (state.expandedBookings[dateId].length === 0) {
                delete state.expandedBookings[dateId];
              }
            } else {
              state.expandedBookings[dateId].push(bookingId);
            }
          },
          false,
          "bookings-calendar/setExpandedBooking",
        ),

      isBookingExpanded: (dateId: string, bookingId: string) => {
        const state = get();
        return state.expandedBookings[dateId]?.includes(bookingId) ?? false;
      },

      registerDate: (dateId: string) =>
        set(
          (state) => {
            if (!state.registeredDates.includes(dateId)) {
              state.registeredDates.push(dateId);
            }
          },
          false,
          "bookings-calendar/registerDate",
        ),

      registerBooking: (dateId: string, bookingId: string) =>
        set(
          (state) => {
            if (!state.registeredBookings[dateId]) {
              state.registeredBookings[dateId] = [];
            }

            if (state.registeredBookings[dateId].includes(bookingId)) {
              return;
            } else {
              state.registeredBookings[dateId].push(bookingId);
            }
          },
          false,
          "bookings-calendar/registerBooking",
        ),

      reset: () =>
        set(
          (state) => {
            state.expandedDays = [];
            state.expandedBookings = {};
          },
          false,
          "bookings-calendar/reset",
        ),

      setRefetchFunction: (fn: () => void) =>
        set(
          (state) => {
            state.refetchFunction = fn;
          },
          false,
          "bookings-calendar/setRefetchFunction",
        ),

      triggerRefetch: () =>
        set(
          (state) => {
            state.refetchFunction?.();
          },
          false,
          "bookings-calendar/triggerRefetch",
        ),

      setSelectedFilter: (filter: string) =>
        set(
          (state) => {
            state.selectedFilter = filter;
          },
          false,
          "bookings-calendar/setSelectedFilter",
        ),
    })),
    {
      name: "BookingsCalendarStore",
      enabled: enableStore,
      store: "BookingsCalendarStore",
    },
  ),
);
